/**
 * useReportSummary Hook
 * React hook for managing AI report summary generation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../config/msalConfig';
import { extractReportData, getDataSummary } from '../services/powerBIDataExtractor';
import { sanitizeDataForAI, getSanitizationPreview } from '../utils/dataSanitizer';
import {
    generateSummaryStream,
    getCachedSummary,
    submitFeedback
} from '../services/aiSummarizationService';

// Status constants
export const SUMMARY_STATUS = {
    IDLE: 'idle',
    CHECKING_CACHE: 'checking_cache',
    EXTRACTING: 'extracting',
    SANITIZING: 'sanitizing',
    GENERATING: 'generating',
    STREAMING: 'streaming',
    COMPLETE: 'complete',
    ERROR: 'error'
};

/**
 * Normalize summary format (frontend)
 * Handles both snake_case and camelCase, converts objects to arrays
 */
function normalizeSummary(summary) {
    if (!summary) {
        return summary;
    }

    // Handle HTML string from Template System (System 1)
    if (typeof summary === 'string') {
        const trimmed = summary.trim();
        if (trimmed.startsWith('<') || trimmed.includes('<div') || trimmed.includes('<p>')) {
            console.log('[Frontend Normalize] Detected HTML summary from Template System');
            return {
                isHtml: true,
                htmlContent: summary,
                headline: 'AI Generated Report',
                keyMetrics: [],
                keyInsights: [],
                areasOfConcern: [],
                recommendations: []
            };
        }
    }

    // If it's already an object but has htmlContent, ensure isHtml is set
    if (typeof summary === 'object' && summary.htmlContent) {
        return {
            ...summary,
            isHtml: true
        };
    }

    if (typeof summary !== 'object') {
        return summary;
    }

    const normalized = { ...summary };

    // Helper: Convert object to array if needed
    const objectToArray = (obj) => {
        if (Array.isArray(obj)) return obj;
        if (!obj || typeof obj !== 'object') return [];

        return Object.entries(obj).map(([key, value]) => {
            if (typeof value === 'string') {
                return value;
            } else if (typeof value === 'object') {
                return value;
            } else {
                return { [key]: value };
            }
        });
    };

    // Handle snake_case → camelCase conversion and object → array conversion
    if (normalized.key_metrics) {
        normalized.keyMetrics = Array.isArray(normalized.key_metrics)
            ? normalized.key_metrics
            : objectToArray(normalized.key_metrics);
        delete normalized.key_metrics;
    } else if (normalized.keyMetrics && !Array.isArray(normalized.keyMetrics)) {
        normalized.keyMetrics = objectToArray(normalized.keyMetrics);
    }

    if (normalized.key_insights) {
        normalized.keyInsights = Array.isArray(normalized.key_insights)
            ? normalized.key_insights
            : objectToArray(normalized.key_insights);
        delete normalized.key_insights;
    } else if (normalized.keyInsights && !Array.isArray(normalized.keyInsights)) {
        normalized.keyInsights = objectToArray(normalized.keyInsights);
    }

    if (normalized.areas_of_concern) {
        normalized.areasOfConcern = Array.isArray(normalized.areas_of_concern)
            ? normalized.areas_of_concern
            : objectToArray(normalized.areas_of_concern);
        delete normalized.areas_of_concern;
    } else if (normalized.areasOfConcern && !Array.isArray(normalized.areasOfConcern)) {
        normalized.areasOfConcern = objectToArray(normalized.areasOfConcern);
    }

    if (normalized.recommendations && !Array.isArray(normalized.recommendations)) {
        normalized.recommendations = objectToArray(normalized.recommendations);
    }

    // Handle dataSources (might be data_sources in snake_case)
    if (normalized.data_sources) {
        normalized.dataSources = normalized.data_sources;
        delete normalized.data_sources;
    }

    // Normalize dataSources field names if needed
    if (normalized.dataSources) {
        const ds = normalized.dataSources;
        if (ds.used_screenshot !== undefined) {
            ds.usedScreenshot = ds.used_screenshot;
            delete ds.used_screenshot;
        }
        if (ds.used_structured_data !== undefined) {
            ds.usedStructuredData = ds.used_structured_data;
            delete ds.used_structured_data;
        }
        if (ds.primary_source !== undefined) {
            ds.primarySource = ds.primary_source;
            delete ds.primary_source;
        }
    }

    console.log('[Frontend Normalize] Summary normalized:', {
        hasKeyMetrics: !!normalized.keyMetrics,
        keyMetricsLength: normalized.keyMetrics?.length,
        hasKeyInsights: !!normalized.keyInsights,
        keyInsightsLength: normalized.keyInsights?.length,
        hasAreasOfConcern: !!normalized.areasOfConcern,
        areasOfConcernLength: normalized.areasOfConcern?.length,
        hasRecommendations: !!normalized.recommendations,
        recommendationsLength: normalized.recommendations?.length,
        hasDataSources: !!normalized.dataSources
    });

    return normalized;
}

/**
 * Hook for managing AI report summary generation
 * @param {Object} options - Hook options
 * @returns {Object} Hook state and methods
 */
export function useReportSummary(options = {}) {
    const { instance, accounts } = useMsal();
    const {
        organizationId,
        datasetId,
        moduleName,
        embedUrl,
        accessToken,
        autoCheckCache = true
    } = options;

    // State
    const [status, setStatus] = useState(SUMMARY_STATUS.IDLE);
    const [summary, setSummary] = useState(null);
    const [streamingContent, setStreamingContent] = useState('');
    const [error, setError] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [metadata, setMetadata] = useState(null);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    // Refs
    const abortControllerRef = useRef(null);
    const reportRef = useRef(null);

    /**
     * Acquire access token for API calls
     */
    const getAccessToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) {
            throw new Error('No authenticated account');
        }

        const request = {
            scopes: protectedResources.api.scopes.read,
            account: accounts[0]
        };

        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            // If silent fails, try interactive
            const response = await instance.acquireTokenPopup(request);
            return response.accessToken;
        }
    }, [instance, accounts]);

    /**
     * Capture current report state (filters, slicers) for accurate screenshot
     */
    const captureReportState = useCallback(async (report, activePage) => {
        try {
            console.log('[AI Summary] Capturing report state (filters, slicers)...');

            const state = {
                filters: {
                    report: [],
                    page: []
                },
                slicers: []
            };

            // Capture report-level filters
            try {
                const reportFilters = await report.getFilters();
                state.filters.report = reportFilters || [];
                console.log(`[AI Summary] ✓ ${state.filters.report.length} report filters`);
            } catch (err) {
                console.warn('[AI Summary] Failed to get report filters:', err?.message || err);
            }

            // Capture page-level filters
            try {
                const pageFilters = await activePage.getFilters();
                state.filters.page = pageFilters || [];
                console.log(`[AI Summary] ✓ ${state.filters.page.length} page filters`);
            } catch (err) {
                console.warn('[AI Summary] Failed to get page filters:', err?.message || err);
            }

            // Capture slicer states
            try {
                const visuals = await activePage.getVisuals();
                const slicers = visuals.filter(v => v.type === 'slicer');

                for (const slicer of slicers) {
                    try {
                        const slicerState = await slicer.getSlicerState();
                        state.slicers.push({
                            name: slicer.name,
                            title: slicer.title,
                            state: slicerState
                        });
                    } catch (err) {
                        console.warn(`[AI Summary] Failed to get slicer ${slicer.name}:`, err.message);
                    }
                }

                console.log(`[AI Summary] ✓ ${state.slicers.length} slicers captured`);
            } catch (err) {
                console.warn('[AI Summary] Failed to get slicers:', err.message);
            }

            console.log('[AI Summary] ✓ Report state captured:', {
                reportFilters: state.filters.report.length,
                pageFilters: state.filters.page.length,
                slicers: state.slicers.length
            });

            return state;
        } catch (error) {
            console.error('[AI Summary] Error capturing report state:', error);
            return null;
        }
    }, []);

    /**
     * Check for cached summary
     */
    const checkCache = useCallback(async (reportId, pageId) => {
        try {
            setStatus(SUMMARY_STATUS.CHECKING_CACHE);
            const token = await getAccessToken();

            const cached = await getCachedSummary(
                reportId,
                pageId,
                organizationId,
                token
            );

            if (cached) {
                // Normalize cached summary (may have snake_case or object format)
                const normalizedSummary = normalizeSummary(cached.summaryContent);
                setSummary(normalizedSummary);
                setFromCache(true);
                setStatus(SUMMARY_STATUS.COMPLETE);
                setMetadata({
                    summaryId: cached.summaryId,
                    generatedAt: cached.generatedAt,
                    expiresAt: cached.expiresAt
                });
                return true;
            }

            return false;
        } catch (error) {
            console.warn('Cache check failed:', error);
            return false;
        }
    }, [getAccessToken, organizationId]);

    /**
     * Generate summary for a report
     */
    const generateSummary = useCallback(async (report, options = {}) => {
        const {
            skipCache = false,
            detailLevel = 'standard',
            includeRecommendations = true
        } = options;

        reportRef.current = report;

        try {
            // Reset state
            setError(null);
            setSummary(null);
            setStreamingContent('');
            setFromCache(false);
            setFeedbackSubmitted(false);
            setMetadata(null);

            // Get report info
            const pages = await report.getPages();
            const activePage = pages.find(p => p.isActive);
            const reportId = report.config?.id;
            const pageId = activePage?.name;
            const reportName = report.config?.settings?.reportName;

            // Check cache first (unless skipped)
            if (!skipCache && autoCheckCache) {
                const hasCached = await checkCache(reportId, pageId);
                if (hasCached) return;
            }

            // Capture report state (filters/slicers) for accurate screenshot
            let reportState = null;
            try {
                reportState = await captureReportState(report, activePage);
                if (reportState) {
                    console.log('[AI Summary] Report state captured for screenshot accuracy');
                }
            } catch (stateError) {
                console.warn('[AI Summary] Failed to capture report state:', stateError);
            }

            // Extract data
            setStatus(SUMMARY_STATUS.EXTRACTING);
            setStatusMessage('Extracting report data...');

            // Check if screenshot capture is enabled via config
            const enableScreenshots = process.env.REACT_APP_ENABLE_AI_SCREENSHOTS !== 'false';

            // Add timeout to prevent hanging (300 seconds)
            const extractionTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Data extraction timeout after 300 seconds')), 300000)
            );

            const extractedData = await Promise.race([
                extractReportData(report, {
                    maxVisuals: 10,
                    maxRowsPerVisual: 150,
                    captureScreenshots: enableScreenshots, // Config-driven screenshot capture
                    optimizeImages: true,
                    maxImageSizeKB: 500,
                    embedUrl: embedUrl,  // Power BI embed URL for screenshot authentication
                    accessToken: accessToken,  // Power BI access token for screenshot authentication
                    datasetId: datasetId, // Pass datasetId for Accuracy Shield reportContext
                    reportState: reportState  // Filter/slicer state for accurate screenshot
                }),
                extractionTimeout
            ]);

            if (enableScreenshots) {
                console.log('[AI Summary] Screenshot capture enabled via config');
            } else {
                console.log('[AI Summary] Screenshot capture disabled via config (text-only mode)');
            }

            // Sanitize data
            setStatus(SUMMARY_STATUS.SANITIZING);
            setStatusMessage('Sanitizing data...');

            const { data: sanitizedData, stats: sanitizationStats } = sanitizeDataForAI(extractedData);

            setMetadata(prev => ({
                ...prev,
                sanitization: sanitizationStats,
                extraction: extractedData.metadata
            }));

            // Generate summary with streaming
            setStatus(SUMMARY_STATUS.GENERATING);
            setStatusMessage('Generating AI summary...');

            const token = await getAccessToken();

            console.log('[useReportSummary] Sending to backend - skipCache:', skipCache);

            abortControllerRef.current = generateSummaryStream(
                {
                    reportId,
                    pageId,
                    activeFilters: sanitizedData.activeFilters, // CRITICAL: Promote filters to root for backend awareness
                    extractedData: {
                        ...sanitizedData,
                        datasetId: datasetId // CRITICAL: Pass datasetId for Accuracy Shield
                    },
                    options: {
                        skipCache,
                        detailLevel,
                        includeRecommendations,
                        moduleName,  // Pass module context for template selection
                        analysisLevel: 'page'  // Part 7: Specify analysis level for template selection
                    },
                    organizationId,
                    reportName,
                    pbiToken: accessToken // Pass Power BI token for recursive Tier 0 grounding
                },
                token,
                {
                    onChunk: (content) => {
                        setStatus(SUMMARY_STATUS.STREAMING);
                        setStreamingContent(prev => prev + content);
                    },
                    onStatus: (message) => {
                        setStatusMessage(message);
                    },
                    onComplete: (result) => {
                        // Normalize summary (may have snake_case or object format)
                        const normalizedSummary = normalizeSummary(result.summary);
                        setSummary(normalizedSummary);
                        setFromCache(result.fromCache || false);
                        setStatus(SUMMARY_STATUS.COMPLETE);
                        setMetadata(prev => ({
                            ...prev,
                            summaryId: result.summaryId,
                            tokensUsed: result.tokensUsed
                        }));
                    },
                    onError: (err) => {
                        setError(err);
                        setStatus(SUMMARY_STATUS.ERROR);
                    }
                }
            );
        } catch (err) {
            console.error('Summary generation error:', err);
            setError(err);
            setStatus(SUMMARY_STATUS.ERROR);
        }
    }, [getAccessToken, organizationId, autoCheckCache, checkCache]);

    /**
     * Cancel ongoing generation
     */
    const cancelGeneration = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setStatus(SUMMARY_STATUS.IDLE);
        setStatusMessage('');
    }, []);

    /**
     * Reset all state
     */
    const reset = useCallback(() => {
        cancelGeneration();
        setSummary(null);
        setStreamingContent('');
        setError(null);
        setFromCache(false);
        setStatusMessage('');
        setMetadata(null);
        setFeedbackSubmitted(false);
    }, [cancelGeneration]);

    /**
     * Submit feedback for the summary
     */
    const sendFeedback = useCallback(async (helpful, rating = null, comment = null) => {
        if (!metadata?.summaryId) {
            throw new Error('No summary ID available');
        }

        try {
            const token = await getAccessToken();

            await submitFeedback({
                summaryId: metadata.summaryId,
                helpful,
                rating,
                comment,
                organizationId
            }, token);

            setFeedbackSubmitted(true);
            return true;
        } catch (error) {
            console.error('Feedback submission error:', error);
            throw error;
        }
    }, [getAccessToken, metadata, organizationId]);

    /**
     * Regenerate summary (skip cache)
     */
    const regenerate = useCallback(async (report, options = {}) => {
        console.log('[useReportSummary] regenerate() called, skipCache: true');
        if (report) {
            await generateSummary(report, {
                ...options,
                skipCache: true
            });
        } else {
            console.error('[useReportSummary] report parameter is missing, cannot regenerate');
        }
    }, [generateSummary]);

    /**
     * Get data preview before generation
     */
    const getPreview = useCallback(async (report) => {
        try {
            const dataSummary = await getDataSummary(report);
            const extractedData = await extractReportData(report);
            const sanitizationPreview = getSanitizationPreview(extractedData);

            return {
                dataSummary,
                sanitizationPreview
            };
        } catch (error) {
            console.error('Preview error:', error);
            throw error;
        }
    }, []);

    /**
     * Auto-check cache on mount if autoCheckCache is enabled
     * This restores summaries when navigating back to a report
     */
    useEffect(() => {
        const autoLoadFromCache = async () => {
            // Only auto-check if we have a report and cache checking is enabled
            if (!autoCheckCache || !reportRef.current) return;

            try {
                const pages = await reportRef.current.getPages();
                const activePage = pages.find(p => p.isActive);
                const reportId = reportRef.current.config?.id;
                const pageId = activePage?.name;

                if (reportId && pageId) {
                    console.log('[useReportSummary] Auto-checking cache on mount for', reportId, pageId);
                    await checkCache(reportId, pageId);
                }
            } catch (error) {
                console.warn('[useReportSummary] Auto-check cache failed:', error);
                // Silent failure - cache check is optional
            }
        };

        autoLoadFromCache();
    }, [autoCheckCache, checkCache]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        // State
        status,
        summary,
        streamingContent,
        error,
        fromCache,
        statusMessage,
        metadata,
        feedbackSubmitted,

        // Computed
        isLoading: [
            SUMMARY_STATUS.CHECKING_CACHE,
            SUMMARY_STATUS.EXTRACTING,
            SUMMARY_STATUS.SANITIZING,
            SUMMARY_STATUS.GENERATING,
            SUMMARY_STATUS.STREAMING
        ].includes(status),
        isComplete: status === SUMMARY_STATUS.COMPLETE,
        isError: status === SUMMARY_STATUS.ERROR,
        canSubmitFeedback: status === SUMMARY_STATUS.COMPLETE && !feedbackSubmitted && metadata?.summaryId,

        // Methods
        generateSummary,
        cancelGeneration,
        reset,
        sendFeedback,
        regenerate,
        getPreview,
        checkCache
    };
}

export default useReportSummary;
